<template>
    <component
        :is="tag"
        v-bind="nonEventAttrs"
        class="inline-flex justify-center items-center border rounded shadow-sm | transition ease-in-out duration-150 | focus:outline-none focus:z-10"
        :style="rootStyle"
        :class="[
            rootClass,
            colorClassName,
            sizeClassName,
            fontSizeClassName,
            {
                'pointer-events-none select-none': loading,
                'cursor-not-allowed': effectivelyDisabled,
                'focus-visible:ring-2 focus-visible:ring-offset-2': !loading && !disabled,
                'has-error': hasError,
            },
        ]"
        :type="type"
        :disabled="nativeDisabled"
        :aria-disabled="ariaDisabledAttribute"
        :loading="loading"
        :tabindex="loading || disabled ? -1 : 0"
        @click.capture="preventInactiveLinkNavigation"
        v-on="listeners"
    >
        <spinner
            class="absolute h-4.5 w-4.5"
            :light="lightSpinner"
            data-testid="spinner"
            v-if="loading"
        />
        <span class="inline-flex justify-center items-center" :class="{ 'opacity-0': loading }">
            <slot />
        </span>
    </component>
</template>

<script setup lang="ts">
    import { computed, inject, useAttrs } from 'vue';
    import Spinner from '@/components/activix-ui/ActivixSpinner/ActivixSpinner.vue';
    import {
        ButtonColor,
        ButtonTag,
        ButtonSize,
        ButtonType,
    } from '@/components/activix-ui/ActivixButton/types.ts';
    import { splitAttrs } from '@/utils/vue-related.ts';

    // Disable automatic attrs fallthrough — listeners are manually bound via v-on="listeners".
    defineOptions({
        inheritAttrs: false,
        compatConfig: { MODE: 3 },
    });

    interface Props {
        color?: ButtonColor;
        disabled?: boolean;
        ariaDisabled?: boolean;
        loading?: boolean;
        tag?: ButtonTag;
        size?: ButtonSize;
        type?: ButtonType;
        hasError?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        color: ButtonColor.White,
        disabled: false,
        ariaDisabled: false,
        loading: false,
        tag: ButtonTag.Button,
        size: ButtonSize.MD,
        type: ButtonType.Button,
        hasError: false,
    });

    const emit = defineEmits<{
        (e: 'click', event: Event): void;
    }>();

    const attrs = useAttrs();
    const oldFontScale = inject<boolean>('oldFontScale', true);

    // Normalize props to lowercase to accept e.g. both 'Blue' and 'blue'.
    const normalizedColor = computed(
        () => (props.color?.toLowerCase() ?? ButtonColor.White) as ButtonColor
    );
    const normalizedSize = computed(
        () => (props.size?.toLowerCase() ?? ButtonSize.MD) as ButtonSize
    );

    const lightSpinner = computed(() => {
        return normalizedColor.value !== ButtonColor.White;
    });

    const effectivelyDisabled = computed(() => props.disabled || props.ariaDisabled);

    const colorClassName = computed(() => {
        switch (normalizedColor.value) {
            case ButtonColor.Blue:
                return effectivelyDisabled.value
                    ? 'text-gray-550 bg-gray-200 border-gray-300 shadow-none'
                    : 'text-white bg-blue-500 border-blue-550 | hover:text-blue-100 hover:bg-blue-550 hover:border-blue-600 | active:text-blue-100 active:bg-blue-600 active:border-blue-700 | focus-visible:ring-blue-500';

            case ButtonColor.Red:
                return effectivelyDisabled.value
                    ? 'text-gray-550 bg-gray-200 border-gray-300 shadow-none'
                    : 'text-white bg-red-500 border-red-550 | hover:text-red-100 hover:bg-red-550 hover:border-red-600 | active:text-red-100 active:bg-red-600 active:border-red-700 | focus-visible:ring-red-500';

            case ButtonColor.White:
            default:
                return effectivelyDisabled.value
                    ? 'text-gray-550 bg-gray-200 border-gray-300 shadow-none'
                    : 'text-gray-700 bg-white border-gray-300 | hover:text-gray-800 hover:bg-gray-100 hover:border-gray-300 | active:bg-gray-150 active:border-gray-350 | focus-visible:ring-blue-500';
        }
    });

    const sizeClassName = computed(() => {
        const sizeClasses: Record<ButtonSize, string> = {
            [ButtonSize.XS]: 'px-2.5 py-1',
            [ButtonSize.SM]: 'px-3 py-1.5',
            [ButtonSize.MD]: 'px-4 py-2',
            [ButtonSize.LG]: 'px-5 py-2.5',
            [ButtonSize.XL]: 'px-6 py-3',
        };
        return sizeClasses[normalizedSize.value];
    });

    const fontSizeClassName = computed(() => {
        const oldFontSizeClasses: Record<ButtonSize, string> = {
            [ButtonSize.XS]: 'text-sm',
            [ButtonSize.SM]: 'text-sm',
            [ButtonSize.MD]: 'text-base',
            [ButtonSize.LG]: 'text-lg',
            [ButtonSize.XL]: 'text-lg',
        };

        const fontSizeClasses: Record<ButtonSize, string> = {
            [ButtonSize.XS]: 'text-xs',
            [ButtonSize.SM]: 'text-xs',
            [ButtonSize.MD]: 'text-sm',
            [ButtonSize.LG]: 'text-base',
            [ButtonSize.XL]: 'text-base',
        };

        return oldFontScale
            ? oldFontSizeClasses[normalizedSize.value]
            : fontSizeClasses[normalizedSize.value];
    });

    const splitComponentAttrs = computed(() => splitAttrs(attrs as Record<string, unknown>));
    const nonEventAttrs = computed(() => {
        const forwardedAttrs = { ...splitComponentAttrs.value.nonEventAttrs };

        delete forwardedAttrs.class;
        delete forwardedAttrs.style;

        return forwardedAttrs;
    });
    const rootClass = computed(() => attrs.class);
    const rootStyle = computed(() => attrs.style);
    const nativeDisabled = computed(() => {
        return props.tag === ButtonTag.Button ? props.disabled || props.loading : undefined;
    });
    const ariaDisabledAttribute = computed(() => {
        if (props.ariaDisabled || (props.tag !== ButtonTag.Button && props.disabled)) {
            return 'true';
        }

        return undefined;
    });

    const preventInactiveLinkNavigation = (event: Event) => {
        const inactiveLink = props.tag !== ButtonTag.Button && (props.disabled || props.loading);
        if (!props.ariaDisabled && !inactiveLink) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
    };

    const onClick = (e: Event) => {
        if (effectivelyDisabled.value || props.loading) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return;
        }

        emit('click', e);
    };

    const listeners = computed(() => {
        return {
            ...splitComponentAttrs.value.listeners,
            click: onClick,
        };
    });
</script>
